/* eslint-disable no-shadow */
import React, { memo, useCallback, useEffect, useState } from "react";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  Grid,
  Modal,
  Typography,
  Checkbox as CheckBox,
} from "@material-ui/core";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
  Settings,
  Restore,
  WidgetsOutlined,
  ArrowUpward,
  ArrowDownward,
} from "@material-ui/icons";
import { v4 as uuid } from "uuid";
import hash from "object-hash";
import { CustomSwitch } from "./Utilities/headlinesUtils";
import { Historical, Delta } from "../QueueLeaderboard";

interface visuals {
  title: string;
  type: string;
  alignment: string;
  color: string;
  bgColor: string;
  bold: boolean;
  includeTarget: boolean;
  includeRange: boolean;
  includeFooter: boolean;
  animateChanges: boolean;
  reflectiveText: boolean;
}

interface data {
  metric: string;
  metricType: string;
  ignoreFilters: boolean;
  desiredTarget?: number | string;
  rangeMin: number;
  rangeMax: number;
  urgent: boolean;
  value: number;
  prevValue: number;
}

interface widget {
  id: string;
  data: data;
  visuals: visuals;
  // used to delete itself in the scope of the widget
  setMappableWidgets?: React.Dispatch<React.SetStateAction<widget[]>>;
  setVisuals?: React.Dispatch<React.SetStateAction<visuals>>;
  setData?: React.Dispatch<React.SetStateAction<data>>;
  display?: boolean;
  populateWidgetCustomizer?: any;
}

const bgFormatter = (props: widget, mode: string) => {
  if (props.visuals.bgColor !== "transparent") {
    return `linear-gradient(0deg, ${props.visuals.bgColor}aa, ${props.visuals.bgColor}bb)`;
  }
  return mode === "light" ? "linear-gradient(0deg, #0684bddd, #0684bd)" : "#181d1f";
};

const bgRangeFormatter = (props: widget, mode: string) => {
  if (props.visuals.includeRange) {
    return bgFromRange(props);
  }
  return mode === "light"
    ? `linear-gradient(180deg, ${props.visuals.bgColor}, ${props.visuals.bgColor}aa 90.71%)`
    : `linear-gradient(0deg, ${props.visuals.bgColor}, ${props.visuals.bgColor}88 90.71%)` ||
    (mode === "light" ? "white" : "#333");
};

const colorFormatter = (props: widget, mode: string) => {
  if (props.visuals.color === "transparent") {
    if (mode === "light") {
      return "#24282be3";
    }
    return "white";
  }
  return props.visuals.color;
};

const transparentColorFormatter = (props: widget, mode: string) => {
  if (props.visuals.color === "transparent") {
    if (mode === "light") {
      return "#56565646";
    }
    return "#ffffff1f";
  }
  return `${props.visuals.color}33`;
};

const borderHandler = (mode: string) => {
  if (mode === "light") {
    return "2px solid #2196f3";
  }
  return "2px solid #cbcbcb";
};

const timeFormatter = (time: number) => {
  const t = Math.abs(time);
  const isNegative = time.toString().startsWith("-");
  const sec = Math.floor(t / 1000);
  const min = Math.floor(sec / 60);
  const formatSec = sec % 60;
  const formatMin = String(min).padStart(2, "0");
  const tostr = String(formatSec).padStart(2, "0");
  return `${isNegative ? "-" : ""}${formatMin}:${tostr}`;
};

const inputStyles = (mode: string) => {
  switch (mode) {
    case "light":
      return {
        backgroundColor: "#fff",
        color: "black",
        border: "1px solid rgb(188 188 188)",
        borderRadius: 4,
        width: "auto",
        padding: 8,
      };
    case "dark":
      return {
        backgroundColor: "#2e2e2e",
        color: "white",
        border: "1px solid #707070",
        borderRadius: 4,
        width: "auto",
        padding: 8,
      };
    default:
      return {
        backgroundColor: "#fff",
        color: "black",
        border: "1px solid #858585",
        borderRadius: 4,
        width: "auto",
        padding: 8,
      };
  }
};

const backlog: Array<Delta> = [];

const bgFromRange = (props: widget) => {
  const halfwayPoint = (props.data.rangeMin + props.data.rangeMax) / 2;
  const numberValue = Number(props.data.value);
  if (numberValue <= props.data.rangeMin) {
    return "linear-gradient(0deg, #e66b6b93 4%, #ffbfbf87 71%)";
  }
  if (numberValue > props.data.rangeMin && numberValue < halfwayPoint) {
    return "linear-gradient(0deg, #e9e47fa2 4%, #f6ffbf8f 71%)";
  }
  if (numberValue >= halfwayPoint && numberValue < props.data.rangeMax) {
    return "linear-gradient(0deg, #7fc9e978 4%, #bfe6ff8d 71%)";
  }
  if (numberValue >= props.data.rangeMax) {
    return "linear-gradient(0deg, #81e97f9a 4%, #bfffc193 71%)";
  }
  return "linear-gradient(0deg, #7fc9e989 4%, #bfe6ff85 71%)";
};

let inProgressDelta: string;

function populateWidgetCustomizer({
  mappableWidgets,
  setVisuals,
  setData,
  setWidgetId,
  id,
}: {
  mappableWidgets: widget[];
  setVisuals: React.Dispatch<React.SetStateAction<visuals>>;
  setData: React.Dispatch<React.SetStateAction<data>>;
  id: string;
  setWidgetId: React.Dispatch<React.SetStateAction<string>>;
}) {
  const getWidget = mappableWidgets?.find((w) => w?.id === id);
  setVisuals(getWidget?.visuals!);
  setData(getWidget?.data!);
  setWidgetId(id);
}

function UserHeadlines({
  displayHeadlines,
  mode,
  leaderboardDeltas,
  historicals,
  sidebar,
 }: {
  displayHeadlines: boolean;
  mode: string;
  leaderboardDeltas: Array<Delta>;
  historicals: Array<Historical>;
  sidebar: boolean;
 }) {
  // parsed users from endpoint
  const [users, setUsers] = useState<Set<string>>();
  const [selectedWidgetId, setSelectedWidgetId] = useState<string | undefined>();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [widgetConfigOpen, setWidgetConfigOpen] = useState<boolean>(false);
  const [widgetVisuals, setWidgetVisuals] = useState<visuals>({
    title: "",
    type: "metric",
    alignment: "center",
    color: "black",
    bgColor: "#ffffff",
    bold: false,
    includeTarget: false,
    includeRange: false,
    includeFooter: false,
    animateChanges: false,
    reflectiveText: false,
  });
  const [widgetData, setWidgetData] = useState<data>({
    metric: "",
    metricType: "",
    ignoreFilters: true,
    desiredTarget: undefined,
    rangeMin: 0,
    rangeMax: 10,
    urgent: false,
    value: 0,
    prevValue: 0,
  });
  const [mappableWidgets, setMappableWidgets] = useState<widget[]>([
    {
      visuals: {
        title: "",
        type: "metric",
        alignment: "center",
        color: "transparent",
        bgColor: "transparent",
        bold: false,
        includeTarget: false,
        includeRange: false,
        includeFooter: false,
        animateChanges: false,
        reflectiveText: false,
      },
      id: uuid(),
      data: {
        metric: "average call duration",
        metricType: "time",
        ignoreFilters: true,
        desiredTarget: undefined,
        rangeMin: 0,
        rangeMax: 10,
        urgent: false,
        value: 0,
        prevValue: 0,
      },
      display: true,
    },
    {
      visuals: {
        title: "",
        type: "metric",
        alignment: "center",
        color: "transparent",
        bgColor: "transparent",
        bold: false,
        includeTarget: false,
        includeRange: false,
        includeFooter: false,
        animateChanges: false,
        reflectiveText: false,
      },
      id: uuid(),
      data: {
        metric: "total duration",
        metricType: "time",
        ignoreFilters: true,
        desiredTarget: undefined,
        rangeMin: 0,
        rangeMax: 10,
        urgent: false,
        value: 0,
        prevValue: 0,
      },
      display: true,
    },
    {
      visuals: {
        title: "",
        type: "metric",
        alignment: "center",
        color: "transparent",
        bgColor: "transparent",
        bold: false,
        includeTarget: false,
        includeRange: false,
        includeFooter: false,
        animateChanges: false,
        reflectiveText: false,
      },
      id: uuid(),
      data: {
        metric: "total call count",
        metricType: "count",
        ignoreFilters: true,
        desiredTarget: undefined,
        rangeMin: 0,
        rangeMax: 10,
        urgent: false,
        value: 0,
        prevValue: 0,
      },
      display: true,
    },
    {
      visuals: {
        title: "",
        type: "metric",
        alignment: "center",
        color: "transparent",
        bgColor: "transparent",
        bold: false,
        includeTarget: false,
        includeRange: false,
        includeFooter: false,
        animateChanges: false,
        reflectiveText: false,
      },
      id: uuid(),
      data: {
        metric: "max ring time inbound",
        metricType: "time",
        ignoreFilters: true,
        desiredTarget: undefined,
        rangeMin: 0,
        rangeMax: 10,
        urgent: false,
        value: 0,
        prevValue: 0,
      },
      display: true,
    },
    {
      visuals: {
        title: "",
        type: "metric",
        alignment: "center",
        color: "transparent",
        bgColor: "transparent",
        bold: false,
        includeTarget: false,
        includeRange: false,
        includeFooter: false,
        animateChanges: false,
        reflectiveText: false,
      },
      id: uuid(),
      data: {
        metric: "max ring time outbound",
        metricType: "time",
        ignoreFilters: true,
        desiredTarget: undefined,
        rangeMin: 0,
        rangeMax: 10,
        urgent: false,
        value: 0,
        prevValue: 0,
      },
      display: true,
    },
    {
      visuals: {
        title: "",
        type: "metric",
        alignment: "center",
        color: "transparent",
        bgColor: "transparent",
        bold: false,
        includeTarget: false,
        includeRange: false,
        includeFooter: false,
        animateChanges: false,
        reflectiveText: false,
      },
      id: uuid(),
      data: {
        metric: "total hangups",
        metricType: "count",
        ignoreFilters: true,
        desiredTarget: undefined,
        rangeMin: 0,
        rangeMax: 10,
        urgent: false,
        value: 0,
        prevValue: 0,
      },
      display: true,
    },
  ]);

  const WidgetCard = memo((props: any) => (
    <Card
      style={{
        borderRadius: 10,
        backgroundColor: mode === "light" ? "" : "#333",
        color: "#fff",
        margin: 5,
        marginLeft: 2,
        marginRight: 2,
        width: 220,
        border: "1px solid #00000022",
      }}
    >
      <div
        className="card-main card-header"
        style={{
          padding: 8,
          background: bgFormatter(props, mode),
          borderBottom: props.visuals.bgColor !== "transparent" ? `1px solid ${props.visuals.bgColor}aa` : "1px solid #00000000",
          height: 25,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          position: 'relative',
          fontFamily: "Arial",
          borderTopRightRadius: 8,
          borderTopLeftRadius: 8,
        }}
      >
        <p
          title={props.visuals.title || props.data.metric}
          style={{
            margin: 0,
            width: "100%",
            display: "flex",
            justifyContent: props.visuals.alignment || "center",
          }}
        >
          <span style={{
            width: 150,
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            overflow: "hidden",
            textAlign: props.visuals.alignment || "center",
            color:
              props.visuals.color === "transparent"
                ? "inherit"
                : props.visuals.color
          }}>{props.visuals.title || props.data.metric}</span>
        </p>
        <span className="gear-blur" style={{
          background: `linear-gradient(0deg, ${props.visuals.bgColor}cc, ${props.visuals.bgColor}bb)`,
          position: "absolute",
          right: 10,
          top: 6,
          width: 24,
          height: 24,
          borderRadius: 50
        }} />
        <button
          type="button"
          className="gear-icon"
          style={{
            background: "transparent",
            paddingRight: 0,
            border: 0,
            cursor: "pointer",
            position: "absolute",
            right: 10,
            top: 6,
            borderRadius: 50,
            zIndex: 400
          }}
          aria-label="close"
          onClick={() => {
            setWidgetConfigOpen(true);
            populateWidgetCustomizer({
              mappableWidgets: props.mappableWidgets,
              setVisuals: props.setVisuals,
              setData: props.setData,
              id: props.id,
              setWidgetId: props.setWidgetId,
            });
          }}
        >
          <Settings style={{ color: "white" }} />
        </button>
      </div>
      <CardContent
        style={{
          background: bgRangeFormatter(props, mode),
          borderBottomLeftRadius: 8,
          borderBottomRightRadius: 8,
        }}
      >
        <Box
          display="flex"
          justifyContent={props.visuals.alignment || "center"}
          alignItems="center"
          height={125}
          style={{ position: "relative" }}
        >
          {props.visuals.includeTarget && (
            <div
              className="target-blip"
              style={{ top: "-10px", left: " -10px" }}
            >
              <span>Target: {props.data.desiredTarget || 0}</span>
            </div>
          )}
          {props.data.metricType === "time" &&
            timeFormatter(props.data.value - props.data.prevValue) !==
            "00:00" &&
            timeFormatter(props.data.value - props.data.prevValue) !==
            "-00:00" &&
            props.data.value !== props.data.prevValue &&
            props.visuals.animateChanges && (
              <div className="animate-blip">
                {timeFormatter(
                  props.data.value - props.data.prevValue
                ).startsWith("-") ? (
                  <ArrowDownward style={{ stroke: "red", strokeWidth: 2 }} />
                ) : (
                  <ArrowUpward style={{ stroke: "green", strokeWidth: 2 }} />
                )}
                <span
                  style={{
                    fontWeight: props.visuals.bold ? "bold" : 'normal',
                    color: colorFormatter(props, mode),
                  }}
                >
                  {props.data.metricType === "time"
                    ? timeFormatter(props.data.value - props.data.prevValue)
                    : props.data.value - props.data.prevValue}
                </span>
              </div>
            )}
          {props.data.metricType === "count" &&
            props.data.value !== props.data.prevValue &&
            props.visuals.animateChanges && (
              <div className="animate-blip">
                {timeFormatter(
                  props.data.value - props.data.prevValue
                ).startsWith("-") ? (
                  <ArrowDownward style={{ stroke: "red", strokeWidth: 2 }} />
                ) : (
                  <ArrowUpward style={{ stroke: "green", strokeWidth: 2 }} />
                )}
                <span
                  style={{
                    fontWeight: "bold",
                    color: colorFormatter(props, mode),
                  }}
                >
                  {props.data.metricType === "time"
                    ? timeFormatter(props.data.value - props.data.prevValue)
                    : props.data.value - props.data.prevValue}
                </span>
              </div>
            )}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <h1
              style={{
                fontSize: "4em",
                margin: 0,
                marginTop: 0,
                padding: 0,
                color: colorFormatter(props, mode),
                fontWeight: props.visuals.bold ? "800" : "400" as any,
              }}
            >
              {props.data.metricType === "time"
                ? timeFormatter(props.data.value)
                : props.data.value}
            </h1>
            <div>
              {props.visuals.reflectiveText && (
                <h1
                  style={{
                    margin: 0,
                    fontSize: "4em",
                    position: "absolute",
                    top: "58%",
                    transform: "rotate(180deg) scaleX(-1)",
                    unicodeBidi: "bidi-override",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundImage: `-webkit-linear-gradient(#00000000 50%, ${transparentColorFormatter(
                      props,
                      mode
                    )})`,
                    fontWeight: props.visuals.bold ? "800" : "400" as any,
                  }}
                >
                  {props.data.metricType === "time"
                    ? timeFormatter(props.data.value)
                    : props.data.value}
                </h1>
              )}
            </div>
          </div>
        </Box>
      </CardContent>
    </Card>
  ));

  const handleResetProperties = useCallback(() => {
    setWidgetVisuals({
      title: "",
      type: "metric",
      alignment: "center",
      color: "transparent",
      bgColor: "transparent",
      bold: false,
      includeTarget: false,
      includeRange: false,
      includeFooter: false,
      animateChanges: false,
      reflectiveText: false,
    });
    setWidgetData((prev) => ({
      ...prev,
      ignoreFilters: true,
      desiredTarget: undefined,
      rangeMin: 0,
      rangeMax: 10,
      urgent: false,
    }));
  }, []);

  const handleDragEnd = useCallback(
    (results: any) => {
      const { source, destination, type } = results;
      const newMappable = [...mappableWidgets];
      if (!destination) {
        return;
      }
      if (
        destination.droppableId === source.droppableId &&
        source.index === destination.index
      ) {
        return;
      }
      if (type !== "group") {
        return;
      }
      const sourceWidget = mappableWidgets.filter((w) => w.display === true)[
        source.index
      ];
      const sourceIndex = newMappable.findIndex(
        (w) => w.id === sourceWidget.id
      );

      newMappable.splice(sourceIndex, 1);
      newMappable.splice(destination.index, 0, sourceWidget);
      newMappable.sort((a, b) => {
        if (a.display && !b.display) {
          return -1;
        }
        if (!a.display && b.display) {
          return 1;
        }
        return 0;
      });
      // keep config in localStorage???
      setMappableWidgets(newMappable);
      localStorage.setItem("mappableWidgets", JSON.stringify(newMappable));
    },
    [mappableWidgets, setMappableWidgets]
  );
  useEffect(() => {
    const storedWidgets = localStorage.getItem("mappableWidgets")
    if (storedWidgets) {
      const parsed = JSON.parse(storedWidgets)
      const newMap = parsed.map((widg: widget) => ({
        ...widg,
        data: {
          ...widg.data,
          value: 0,
          prevValue: 0
        }

      })) as widget[]
      setMappableWidgets(newMap)
    }
  }, [])

  useEffect(() => {
    if (users !== undefined) {
      return;
    }
    if (historicals[0] === undefined) {
      return;
    }
    setUsers(new Set(historicals[0].user.map((user) => user.id)));
  }, [users, historicals]);

  // deltas
  useEffect(() => {
    if (leaderboardDeltas.length === 0) {
      return;
    }
    leaderboardDeltas.forEach((data: Delta) => {

      // due to the way deltas are incremented, sometimes there are duplicates
      if (inProgressDelta !== undefined && inProgressDelta === hash(data)) {
        return;
      }

      inProgressDelta = hash(data);

      if (users === undefined) {
        backlog.push(data);
        return;
      }

      const processDelta = (data: Delta) => {
        setMappableWidgets((prevWidgets: widget[]) => {
          const totalCountWidget =
            prevWidgets.find(
              (widget) => widget.data.metric === "total call count"
            )?.data.value || 0;
          const totalDurationWidget =
            prevWidgets.find((widget) => widget.data.metric === "total duration")
              ?.data.value || 0;
          return prevWidgets.map((widget) => {
            if (widget.data.metric === "total call count") {
              return {
                ...widget,
                data: {
                  ...widget.data,
                  value: data.count
                    ? widget.data.value + data.count
                    : widget.data.value,
                  prevValue: widget.data.value,
                },
              };
            }
            if (widget.data.metric === "total duration") {
              return {
                ...widget,
                data: {
                  ...widget.data,
                  value: data.duration
                    ? widget.data.value + data.duration
                    : widget.data.value,
                  prevValue: widget.data.value,
                },
              };
            }
            if (widget.data.metric === "average call duration") {
              return {
                ...widget,
                data: {
                  ...widget.data,
                  value:
                    (data.duration
                      ? Math.floor(data.duration + totalDurationWidget)
                      : Math.floor(
                        Number(
                          totalDurationWidget === 0 ? 1 : totalDurationWidget
                        )
                      )) /
                    (data.count
                      ? Math.floor(Number(data.count + totalCountWidget))
                      : Math.floor(
                        Number(totalCountWidget === 0 ? 1 : totalCountWidget)
                      )),
                  prevValue: Math.floor(
                    Number(totalDurationWidget === 0 ? 1 : totalDurationWidget) /
                    Math.floor(
                      Number(totalCountWidget === 0 ? 1 : totalCountWidget)
                    )
                  ),
                },
              };
            }
            if (widget.data.metric === "max ring time inbound") {
              return {
                ...widget,
                data: {
                  ...widget.data,
                  value: data.maxRingTimeInbound
                    ? widget.data.value + data.maxRingTimeInbound
                    : widget.data.value,
                  prevValue: widget.data.value,
                },
              };
            }
            if (widget.data.metric === "max ring time outbound") {
              return {
                ...widget,
                data: {
                  ...widget.data,
                  value: data.maxRingTimeOutbound
                    ? widget.data.value + data.maxRingTimeOutbound
                    : widget.data.value,
                  prevValue: widget.data.value,
                },
              };
            }
            if (widget.data.metric === "total hangups") {
              return {
                ...widget,
                data: {
                  ...widget.data,
                  value: data.hangupCount
                    ? widget.data.value + data.hangupCount
                    : widget.data.value,
                  prevValue: widget.data.value,
                },
              };
            }
            return widget;
          });
        });
      };

      backlog.forEach((delta) => {
        if (!users?.has(delta.userId)) {
          return;
        }
        processDelta(delta);
      });

      backlog.length = 0;

      if (!users?.has(data.userId)) {
        return;
      }
      processDelta(data);
    });

  }, [leaderboardDeltas, users]);

  const handleColorChange = useCallback((e: any) => {
    setWidgetVisuals((prev) => ({
      ...prev,
      color: e.target.value,
    }));
  }, []);

  return (
    <div className={sidebar ? "kpi-wrapper sidebar-open" : "kpi-wrapper"}>
      <Box
        className={
          mode === "light" ? "OUTER-KPI-BOX light" : "OUTER-KPI-BOX dark"
        }
        style={{ background: mode === "light" ? "white" : "#333" }}
      >
        <div className="INNER-KPI-DIV">
          <Accordion
            elevation={0}
            expanded={displayHeadlines}
            defaultExpanded={displayHeadlines}
            style={{
              backgroundColor: mode === "light" ? "white" : "rgb(24, 24, 24)",
              position: "relative",
            }}
          >
            <AccordionDetails
              className="headlines-master-container"
              style={{
                border:
                  mode === "light" ? "1px solid #babfc7" : "1px solid #68686e",
                paddingRight: drawerOpen ? 282 : 30,
                paddingBottom: 0,
                paddingLeft: 0,
                paddingTop: 0
              }}
            >
              <DragDropContext
                onDragEnd={(e: any) => {
                  handleDragEnd(e);
                }}
              >
                <Droppable
                  droppableId="headlines"
                  type="group"
                  direction="horizontal"
                >
                  {(provided: any) => (
                    <div
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      style={{
                        display: "flex",
                        overflowX: "scroll",
                        width: "100%",
                        paddingBottom: 15,
                        height: 220,
                      }}
                    >
                      {mappableWidgets
                        .filter((w) => w.display === true)
                        .map((widget: any, i: number) => (
                          <Draggable
                            draggableId={widget.id.toString()}
                            key={widget.id.toString()}
                            index={i}
                          >
                            {(providedInner: any) => (
                              <Grid
                                className="card-main"
                                item
                                ref={providedInner.innerRef}
                                // eslint-disable-next-line react/jsx-props-no-spreading
                                {...providedInner.draggableProps}
                                // eslint-disable-next-line react/jsx-props-no-spreading
                                {...providedInner.dragHandleProps}
                              >
                                <WidgetCard
                                  mode={mode}
                                  // eslint-disable-next-line react/jsx-props-no-spreading
                                  {...widget}
                                  mappableWidgets={mappableWidgets}
                                  setMappableWidgets={setMappableWidgets}
                                  setVisuals={setWidgetVisuals}
                                  setData={setWidgetData}
                                  setWidgetId={setSelectedWidgetId}
                                  setWidgetConfig={setWidgetConfigOpen}
                                  populateWidgetCustomizer={
                                    populateWidgetCustomizer
                                  }
                                  id={widget.id}
                                />
                              </Grid>
                            )}
                          </Draggable>
                        ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
              <div
                className={
                  displayHeadlines
                    ? "headlines-menu-container-open"
                    : "headlines-menu-container-closed"
                }
                style={{
                  overflowY: displayHeadlines ? "visible" : "hidden",
                  display: displayHeadlines ? "block" : "hidden",
                  position: "absolute",
                  top: "-0px",
                  right: 0,
                  width: 30,
                  height: displayHeadlines ? 235 : 0,
                  backgroundColor: mode === "light" ? "white" : "#181d1f",
                  border:
                    mode === "light"
                      ? "1px solid #babfc7"
                      : "1px solid #68686e",
                }}
              >
                <div
                  tabIndex={0}
                  role="button"
                  className="headlines-title"
                  style={{
                    overflowY: displayHeadlines ? "visible" : "hidden",
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "center",
                    flexDirection: "column",
                    alignItems: "center",
                    marginTop: 20,
                    borderLeft: drawerOpen
                      ? borderHandler(mode)
                      : "2px solid transparent",
                    transitionDuration: "0.2s",
                    animation: "fadein 0.2s ease forwards",
                  }}
                  onClick={() => {
                    setDrawerOpen((prev) => !prev);
                  }}
                  onKeyDown={() => {
                    setDrawerOpen((prev) => !prev);
                  }}
                >
                  <WidgetsOutlined
                    className="icon"
                    style={{
                      marginTop: 15,
                      color: mode === "light" ? "black" : "white",
                      transform: "scale(0.8)",
                    }}
                    fontSize="small"
                  />
                  <p
                    className="text"
                    style={{
                      overflowY: displayHeadlines ? "visible" : "hidden",
                      color: mode === "light" ? "black" : "white",
                      writingMode: "vertical-rl",
                      textOrientation: "mixed",
                      fontSize: "0.8em",
                      userSelect: "none",
                      margin: 0,
                      paddingTop: 4,
                      paddingLeft: 0,
                      height: 72,
                    }}
                  >
                    Widgets
                  </p>
                </div>
                {drawerOpen && (
                  <div
                    style={{
                      borderLeft:
                        mode === "light"
                          ? "1px solid #babfc7"
                          : "1px solid #68686e",
                      borderRight:
                        mode === "light"
                          ? "1px solid #babfc7"
                          : "1px solid #68686e",
                      borderBottom:
                        mode === "light"
                          ? "1px solid #babfc7"
                          : "1px solid #68686e",
                      backgroundColor: mode === "light" ? "white " : "#222628",
                      position: "absolute",
                      right: "30px",
                      top: "0px",
                      width: 226,
                      height: 239,

                      // height: "calc(100% - 28px)",
                      padding: 12,
                      overflowY: "scroll",
                    }}
                  >
                    {mappableWidgets.map((widget) => (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          height: 24,
                        }}
                      >
                        <CheckBox
                          disableRipple
                          className="checkbox-mui"
                          checked={widget.display}
                          value={widget.id || undefined}
                          onClick={() => {
                            setMappableWidgets((prev) => {
                              const newMap = prev.map((prevWidget) =>
                                prevWidget.id === widget.id
                                  ? {
                                    ...prevWidget,
                                    display: !prevWidget.display,
                                  }
                                  : prevWidget
                              )
                              localStorage.setItem("mappableWidgets", JSON.stringify(newMap));
                              return newMap
                            }
                            );
                          }}
                          style={{
                            color: mode === "light" ? "#2196f3" : "white",
                            padding: 0,
                            height: 13,
                            width: 16,
                          }}
                          size="medium"
                        />

                        {/* <DragIndicator fontSize="small"
                        style={{ transform: "rotate(90deg)", marginRight: 10, color: 'white' }}
                      />{" "} */}
                        <p
                          style={{
                            paddingLeft: 12,
                            color: mode === "light" ? "#222222" : "white",
                            fontSize: "12px",
                            fontWeight: 100,
                            margin: 0,
                            fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Oxygen-Sans,Ubuntu,Cantarell,"Helvetica Neue",sans-serif'
                          }}
                        >
                          {widget.data.metric}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </AccordionDetails>
          </Accordion>
        </div>
      </Box>
      <Modal
        open={widgetConfigOpen}
        style={{ top: "8%", overflow: "scroll", overflowX: "hidden", zIndex: 9999999999 }}
      >
        <>
        <div style={{zIndex: -1, width: '100vw', height: '100%', overflow: 'hidden', background: '#0006', position: 'absolute', left: 0, top: 0}} />
        <div
          className="menu-container container-main "
          style={{
            width: "100%",
            outline: "none",
            backgroundColor: mode === "light" ? "white" : "#333",
            color: mode === "light" ? "#333" : "white",
          }}
        >
          <div
            className="row menu-container header-row"
            style={{
              position: "sticky",
              top: "0",
              width: "inherit",
              zIndex: 11,
              background:
                mode === "light"
                  ? "#0684bd"
                  : "linear-gradient(0deg, rgb(24, 24, 24), rgb(41, 41, 41))",
            }}
          >
            <h2 style={{ margin: 10 }}>Editor</h2>
          </div>
          <Grid container style={{ overflowY: "scroll" }}>
            <Grid
              item
              md={3}
              xs={12}
              className="config-col col-12 col-sm-4 col-md-3"
              style={{
                backgroundColor: mode === "light" ? "white" : "#333",
              }}
            >
              <Typography variant="h5" style={{ marginBottom: "0.8em" }}>
                Visualization
              </Typography>
              <div className="menu-box">
                <div className={`widget-customizer-form-container ${mode === "light" ? "light-mode" : "dark-mode"}`}>
                  <span style={{ color: mode === "light" ? "black" : "white" }}>
                    Title
                  </span>
                  <input
                    id="input-text"
                    color="secondary"
                    style={inputStyles(mode)}
                    type="text"
                    onChange={(e) =>
                      setWidgetVisuals({
                        ...widgetVisuals,
                        title: e.currentTarget.value,
                      })
                    }
                    className="headline-form-control"
                  />
                </div>
                <div className={`widget-customizer-form-container ${mode === "light" ? "light-mode" : "dark-mode"}`}>
                  <span className="form-label">Alignment</span>
                  <div className="button-groups ">
                    <button
                      type="button"
                      value="start"
                      onClick={() =>
                        setWidgetVisuals({
                          ...widgetVisuals,
                          alignment: "start",
                        })
                      }
                      className={`${mode === "dark" && "dark-mode-button"} ${widgetVisuals.alignment === "start"
                          ? "button button-selected"
                          : "button"
                        }`}
                    >
                      Left
                    </button>
                    <button
                      type="button"
                      value="center"
                      onClick={() =>
                        setWidgetVisuals({
                          ...widgetVisuals,
                          alignment: "center",
                        })
                      }
                      className={`${mode === "dark" && "dark-mode-button"} ${widgetVisuals.alignment === "center"
                          ? "button button-selected"
                          : "button"
                        }`}
                    >
                      Center
                    </button>
                    <button
                      type="button"
                      value="end"
                      onClick={() =>
                        setWidgetVisuals({
                          ...widgetVisuals,
                          alignment: "end",
                        })
                      }
                      className={`${mode === "dark" && "dark-mode-button"} ${widgetVisuals.alignment === "end"
                          ? "button button-selected"
                          : "button"
                        }`}
                    >
                      Right
                    </button>
                  </div>
                </div>

                {/* <div className="mt-3">
                  <div className="form-check form-switch p-0">
                    <span>Enable range</span>
                    <input
                      style={{
                        float: "right",
                        paddingLeft: 20,
                        paddingRight: 20,
                        paddingTop: 20,
                      }}
                      onChange={() =>
                        setWidgetVisuals({
                          ...widgetVisuals,
                          includeRange: !widgetVisuals.includeRange,
                        })
                      }
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      checked={widgetVisuals.includeRange}
                    />
                  </div>
                </div> */}
                <div className={`widget-customizer-form-container ${mode === "light" ? "light-mode" : "dark-mode"}`}

                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginTop: "0.5em",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <span className="form-label">Text Color</span>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <button
                        type="button"
                        style={{
                          background: "transparent",
                          border: 0,
                          width: 40,
                        }}
                        onClick={() =>
                          setWidgetVisuals({
                            ...widgetVisuals,
                            color: "transparent",
                          })
                        }
                      >
                        <Restore
                          fontSize="small"
                          style={{
                            color: mode === "light" ? "black" : "white",
                            marginRight: 10,
                            cursor: "pointer",
                          }}
                        />
                      </button>
                      <input
                        style={{ cursor: "pointer" }}
                        value={widgetVisuals.color}
                        type="color"
                        onChange={handleColorChange}
                      />
                    </div>
                  </div>
                </div>
                <div
                  className={`widget-customizer-form-container ${mode === "light" ? "light-mode" : "dark-mode"}`}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginTop: "0.5em",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <span className="form-label">Background</span>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <button
                        type="button"
                        style={{
                          background: "transparent",
                          border: 0,
                          width: 40,
                        }}
                        onClick={() =>
                          setWidgetVisuals({
                            ...widgetVisuals,
                            bgColor: "transparent",
                          })
                        }
                      >
                        <Restore
                          fontSize="small"
                          style={{
                            color: mode === "light" ? "black" : "white",
                            marginRight: 10,
                            cursor: "pointer",
                          }}
                        />
                      </button>
                      <input
                        value={widgetVisuals.bgColor}
                        type="color"
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setWidgetVisuals({
                            ...widgetVisuals,
                            bgColor: (e.target as HTMLInputElement).value,
                          })
                        }
                      />
                    </div>
                  </div>
                </div>
                <div style={{ marginTop: "1.2em" }}>
                  <div className="form-check form-switch p-0">
                    <span
                      data-toggle="tooltip"
                      title="Choose if presented at the front of the list"
                    >
                      Include Target
                    </span>
                    <CustomSwitch
                      checked={widgetVisuals.includeTarget}
                      onChange={() =>
                        setWidgetVisuals({
                          ...widgetVisuals,
                          includeTarget: !widgetVisuals.includeTarget,
                        })
                      }
                    />
                  </div>
                </div>
                <div style={{ marginTop: "1.2em" }}>
                  <div className="form-check form-switch p-0">
                    <span
                      data-toggle="tooltip"
                      title="Choose if presented at the front of the list"
                    >
                      Reflective Text
                    </span>
                    <CustomSwitch
                      checked={widgetVisuals.reflectiveText}
                      onChange={() =>
                        setWidgetVisuals({
                          ...widgetVisuals,
                          reflectiveText: !widgetVisuals.reflectiveText,
                        })
                      }
                    />
                  </div>
                </div>
                <div style={{ marginTop: "1.2em" }}>
                  <div className="form-check form-switch p-0">
                    <span
                      data-toggle="tooltip"
                      title="Choose if presented at the front of the list"
                    >
                      Bold Font
                    </span>
                    <CustomSwitch
                      checked={widgetVisuals.bold}
                      onChange={() =>
                        setWidgetVisuals({
                          ...widgetVisuals,
                          bold: !widgetVisuals.bold
                        })
                      }
                    />
                  </div>
                </div>
                {widgetVisuals.type === "user" && (
                  <>
                    <div className="mt-2">
                      <div className="form-check form-switch p-0">
                        <span className="form-label">
                          Include profile picture
                        </span>
                        <input
                          style={{
                            float: "right",
                            paddingLeft: 20,
                            paddingRight: 20,
                            paddingTop: 20,
                          }}
                          onChange={() =>
                            setWidgetVisuals({
                              ...widgetVisuals,
                              includeTarget: !widgetVisuals.includeTarget,
                            })
                          }
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          checked={widgetVisuals.includeTarget}
                        />
                      </div>
                    </div>
                    <div className="mt-2">
                      <div className="form-check form-switch p-0">
                        <span className="form-label">
                          Include activity status
                        </span>
                        <input
                          style={{
                            float: "right",
                            paddingLeft: 20,
                            paddingRight: 20,
                            paddingTop: 20,
                          }}
                          onChange={() =>
                            setWidgetVisuals({
                              ...widgetVisuals,
                              includeTarget: !widgetVisuals.includeTarget,
                            })
                          }
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          checked={widgetVisuals.includeTarget}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </Grid>
            <Grid
              item
              md={6}
              xs={12}
              className="col-12 widget-col  col-sm-4 col-md-6"
              style={{
                backgroundColor: mode === "light" ? "white" : "#444",
              }}
            >
              <div
                className="widget-container container"
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <div className="widget-inner-box">
                  <div style={{ transform: "scale(1.3)" }}>
                    <WidgetCard
                      visuals={widgetVisuals}
                      data={widgetData}
                      id=""
                      value={widgetData.value}
                      setMappableWidgets={setMappableWidgets}
                    />
                  </div>
                </div>
              </div>
            </Grid>
            <Grid
              item
              md={3}
              xs={12}
              className="col-12 col-sm-4  col-md-3 config-col"
              style={{
                backgroundColor: mode === "light" ? "white" : "#333",
              }}
            >
              <Typography variant="h5" style={{ marginBottom: "0.8em" }}>
                Data
              </Typography>
              <div className="menu-box">
                <div className={`widget-customizer-form-container ${mode === "light" ? "light-mode" : "dark-mode"}`}>
                  <span className="form-label">Type</span>
                  <div className="button-groups">
                    <button
                      type="button"
                      value="metric"
                      onClick={() =>
                        setWidgetVisuals({
                          ...widgetVisuals,
                          type: "metric",
                        })
                      }
                      className={` ${mode === "dark" && "dark-mode-button"} ${widgetVisuals.type === "metric"
                          ? "button button-selected"
                          : "button"
                        }`}
                    >
                      Metric
                    </button>
                    {/* <button
                      value="user"
                      onClick={(e) => handleFormChanges(e, "type")}
                      className={`${mode === "dark" && "dark-mode-button"} ${
                        widgetVisuals.type === "user"
                          ? "button button-selected"
                          : "button"
                      }`}
                    >
                      User
                    </button> */}
                    <button
                      type="button"
                      title="coming soon"
                      disabled
                      style={{
                        cursor: "not-allowed",
                        backgroundColor: "none !important",
                      }}
                      value="graph"
                      onClick={() =>
                        setWidgetVisuals({
                          ...widgetVisuals,
                          type: "graph",
                        })
                      }
                      className={`${mode === "dark" && "dark-mode-button"} ${widgetVisuals.type === "graph"
                          ? "button button-selected"
                          : "button"
                        }`}
                    >
                      Graph
                    </button>
                  </div>
                </div>
                <div style={{ marginTop: "0.5em" }} className="mt-2">
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <span className="form-label">Desired target</span>
                    <input
                      defaultValue={widgetData.desiredTarget}
                      style={inputStyles(mode)}
                      type="text"
                      onChange={(e) =>
                        setWidgetData({
                          ...widgetData,
                          desiredTarget: e.currentTarget.value,
                        })
                      }
                    />
                  </div>
                </div>
                {/* <div className="mt-2">
                  <div className=" row">
                    <div className="col-6 d-flex">
                      <div className="form-number p-0">
                        <label
                          className={
                            widgetData.rangeMin >= widgetData.rangeMax
                              ? "text-danger"
                              : ""
                          }
                        >
                          Min Range
                        </label>
                        <input
                          style={{ float: "right" }}
                          className="input-range"
                          onChange={(e) =>
                            setWidgetData({
                              ...widgetData,
                              rangeMin: Number(e.currentTarget.value),
                            })
                          }
                          min="0"
                          max="10"
                          defaultValue={widgetData.rangeMin}
                          type="number"
                        />
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-number p-0">
                        <label
                          className={
                            widgetData.rangeMin >= widgetData.rangeMax
                              ? "text-danger"
                              : ""
                          }
                        >
                          Max Range
                        </label>
                        <input
                          style={{ float: "right" }}
                          className="input-range"
                          onChange={(e) =>
                            setWidgetData({
                              ...widgetData,
                              rangeMax: Number(e.currentTarget.value),
                            })
                          }
                          min="0"
                          max="10"
                          defaultValue={widgetData.rangeMax}
                          type="number"
                        />
                      </div>
                    </div>
                  </div>
                </div> */}
                {/* <div style={{marginTop: '1.2em'}} className="mt-3">
                  <div className="form-check form-switch p-0">
                    <span>Ignore filters</span>
                    <CustomSwitch
                      checked={widgetData.ignoreFilters}
                      onChange={() =>
                        setWidgetData({
                          ...widgetData,
                          ignoreFilters: !widgetData.ignoreFilters,
                        })
                      }
                    />
                  </div>
                </div> */}
                <div style={{ marginTop: "1.2em" }}>
                  <div className="form-check form-switch p-0">
                    <span data-toggle="tooltip">Enable Change Identifiers</span>
                    <CustomSwitch
                      checked={widgetVisuals.animateChanges}
                      onChange={() =>
                        setWidgetVisuals({
                          ...widgetVisuals,
                          animateChanges: !widgetVisuals.animateChanges,
                        })
                      }
                    />
                  </div>
                </div>
                {/* <div  style={{marginTop: '1.2em'}}>
                  <div className="form-check form-switch p-0">
                    <span
                      data-toggle="tooltip"
                      title="Choose if presented at the front of the list"
                    >
                      Urgent
                    </span>
                    <CustomSwitch
                      checked={widgetData.urgent}
                      onChange={() =>
                        setWidgetData({
                          ...widgetData,
                          urgent: !widgetData.urgent,
                        })
                      }
                    />
                  </div>
                </div> */}
              </div>
            </Grid>
          </Grid>
          <div
            style={{
              margin: "0.8em",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <div style={{ marginBottom: "1em" }} className="">
              <button
                type="button"
                className="btn btn-outline-danger"
                onClick={() => {
                  let newWidgetMap;
                  setMappableWidgets((prevWidgets: widget[]) => {
                    const widgetIndex = prevWidgets.findIndex(
                      (w) => w.id === selectedWidgetId
                    );
                    if (widgetIndex !== -1) {
                      const updatedWidget = {
                        ...prevWidgets[widgetIndex],
                        visuals: {
                          title: "",
                          type: "metric",
                          alignment: "center",
                          color: "transparent",
                          bgColor: "transparent",
                          bold: false,
                          includeTarget: false,
                          includeRange: false,
                          includeFooter: false,
                          animateChanges: false,
                          reflectiveText: false,
                        },
                        data: widgetData,
                      };
                      newWidgetMap = [
                        ...prevWidgets.slice(0, widgetIndex),
                        updatedWidget,
                        ...prevWidgets.slice(widgetIndex + 1),
                      ]
                      localStorage.setItem("mappableWidgets", JSON.stringify(newWidgetMap))
                      return newWidgetMap;
                    }
                    newWidgetMap = [
                      ...prevWidgets,
                      {
                        id: selectedWidgetId!,
                        visuals: widgetVisuals,
                        data: {
                          ...widgetData,
                          value: prevWidgets[widgetIndex].data.value,
                        },
                      },
                    ]
                    localStorage.setItem("mappableWidgets", JSON.stringify(newWidgetMap))
                    return newWidgetMap;
                  });
                  handleResetProperties();
                }}
              >
                Reset to Default
              </button>
            </div>
            <div>
              <button
                type="button"
                className="btn btn-secondary mx-2"
                onClick={() => {
                  handleResetProperties();
                  setWidgetConfigOpen(false);
                }}
              >
                Exit
              </button>
              <button
                className="btn btn-primary"
                type="button"
                onClick={() => {
                  let newWidgetMap;
                  setMappableWidgets((prevWidgets: widget[]) => {
                    const widgetIndex = prevWidgets.findIndex(
                      (w) => w.id === selectedWidgetId
                    );
                    if (widgetIndex !== -1) {
                      const updatedWidget = {
                        ...prevWidgets[widgetIndex],
                        visuals: widgetVisuals,
                        data: {
                          ...widgetData,
                          value: prevWidgets[widgetIndex].data.value,
                        },
                      };
                      newWidgetMap = [
                        ...prevWidgets.slice(0, widgetIndex),
                        updatedWidget,
                        ...prevWidgets.slice(widgetIndex + 1),
                      ]
                      localStorage.setItem("mappableWidgets", JSON.stringify(newWidgetMap))
                      return newWidgetMap;
                    }
                    newWidgetMap = [
                      ...prevWidgets,
                      {
                        id: selectedWidgetId!,
                        visuals: widgetVisuals,
                        data: widgetData,
                      },
                    ];
                    return newWidgetMap
                  });
                  handleResetProperties();
                  setWidgetConfigOpen(false);
                  setSelectedWidgetId(undefined);
                  localStorage.setItem("mappableWidgets", JSON.stringify(newWidgetMap));
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
        </>
      </Modal>
    </div>
  );
}

export default UserHeadlines;
